import { useState, useMemo, useEffect } from "react";
import { Search, User, ShoppingBag } from "lucide-react";
import PageHeader from "../../../components/ui/PageHeader";
import { useAuth } from "../../../auth/AuthContext";
import { useToast } from "../../../hooks/useToast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { format, parseISO, isValid, parse, startOfDay, endOfDay, startOfMonth, endOfMonth } from "date-fns";
import { getSoldProducts, SoldProductBooking, SoldProduct } from "../../../services/BookingsApi";
import { ReportDateRangePicker } from "../../../components/reports/ReportDateRangePicker";

interface SoldProductItem {
  id: number;
  bookingId: number;
  orderNumber: string;
  date: string;
  dateObj: Date | null;
  customerName: string;
  productName: string;
  productImage: string;
  quantity: number;
  unitPrice: number;
  discount: number;
  discountType: string;
  tax: number;
  totalPrice: number;
  status: string;
  paymentMethod: string;
  employeeName: string;
  location: string;
  /** 1 = used in store; show "Used" in Store usage column */
  inStoreUsage?: number;
}

const SoldProductsPage = () => {
  const { user } = useAuth();
  const currencySymbol = user?.company?.currency_symbol || user?.company?.currency?.currency_symbol || "£";
  const toast = useToast();

  const [sales, setSales] = useState<SoldProductItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Filters — default to this month so the list isn't "all time" on load
  const [dateRange, setDateRange] = useState<{ start: Date | null; end: Date | null }>(() => {
    const now = new Date();
    return {
      start: startOfMonth(now),
      end: endOfMonth(now),
    };
  });
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [employeeFilter, setEmployeeFilter] = useState<string>("all");
  const [locationFilter, setLocationFilter] = useState<string>("all");
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<string>("all");
  const [discountTypeFilter, setDiscountTypeFilter] = useState<string>("all");

  // Fetch sold products from API
  useEffect(() => {
    const fetchSoldProducts = async () => {
      try {
        setIsLoading(true);
        const response = await getSoldProducts();

        // Transform booking data into individual product line items
        const transformedSales: SoldProductItem[] = [];

        // Normalize any API payment string to Cash/Card/Cheque/Multiple
          const normalizePayment = (raw: string | null | undefined): string | null => {
            if (raw == null || String(raw).trim() === "") return null;
            // Backend may send e.g. "120.00 (Paid by Cash)" with or without surrounding quotes
            let s = String(raw).replace(/^"+|"+$/g, "").trim();
            if (s === "") return null;
            const lower = s.toLowerCase();
            if (lower.includes("cash")) return "Cash";
            if (lower.includes("card")) return "Card";
            if (lower.includes("cheque")) return "Cheque";
            if (lower.includes("multiple")) return "Multiple";
            if (lower.includes("store")) return "Store Usage";
            if (lower === "null" || lower === "undefined") return null;
            return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
          };

          response.data.forEach((booking: SoldProductBooking) => {
          // Resolve payment method from booking: try all common API field names
          const b = booking as unknown as Record<string, unknown>;
          let rawPayment: string | null = null;
          if (booking.payment_gateway != null && String(booking.payment_gateway).trim() !== "")
            rawPayment = String(booking.payment_gateway).trim();
          else if (booking.payment_type != null && String(booking.payment_type).trim() !== "")
            rawPayment = String(booking.payment_type).trim();
          else if (booking.booking_payments?.[0]) {
            const t = booking.booking_payments[0].type ?? booking.booking_payments[0].payment_type;
            if (t != null && String(t).trim() !== "") rawPayment = String(t).trim();
          }
          if (rawPayment == null && b?.payment_method != null && String(b.payment_method).trim() !== "")
            rawPayment = String(b.payment_method).trim();
          const resolvedPayment = rawPayment ? normalizePayment(rawPayment) : null;

          // Handle both array and object formats for products
          const productsArray = Array.isArray(booking.products)
            ? booking.products
            : Object.values(booking.products);

          productsArray.forEach((product: SoldProduct) => {
            // Calculate tax from product_taxes array
            let totalTaxPercent = 0;
            if (product.product_taxes && product.product_taxes.length > 0) {
              totalTaxPercent = product.product_taxes.reduce((sum, taxItem) => {
                return sum + (taxItem.tax?.percent || 0);
              }, 0);
            }

            // Calculate tax amount based on product price and tax percentage
            const productPrice = product.converted_discounted_price || product.converted_price;
            const tax = (productPrice * totalTaxPercent) / 100;

            // Prefer actual sold unit_price from booking item data; fall back to catalog price
            const unitPrice =
              product.pivot?.unit_price ??
              product.booking_unit_price ??
              (product as any).unit_price ??
              product.converted_discounted_price ??
              product.converted_price;
            const discount = product.discount;

            // Payment method: Store Usage takes precedence (per product), else use resolved booking payment
            let paymentMethod: string;
            if (product.in_store_usage === 1) {
              paymentMethod = "Store Usage";
            } else if (resolvedPayment) {
              paymentMethod = resolvedPayment;
            } else if (booking.amount_paid > 0 && booking.amount_paid < booking.amount_to_pay) {
              paymentMethod = "Multiple";
            } else {
              paymentMethod = "Cash";
            }

            // Get employee name
            const employeeName = booking.employees && booking.employees.length > 0
              ? booking.employees[0].name
              : "No Employee";

            // Parse date for filtering
            let dateObj: Date | null = null;
            try {
              const parsed = parse(booking.date_time, "dd MMM yyyy hh:mm a", new Date());
              dateObj = isValid(parsed) ? parsed : null;
            } catch {
              const d = new Date(booking.date_time);
              dateObj = !isNaN(d.getTime()) ? d : null;
            }

            transformedSales.push({
              id: product.id,
              bookingId: booking.id,
              orderNumber: booking.id.toString(),
              date: booking.date_time,
              dateObj,
              customerName: booking.name,
              productName: product.name,
              productImage: product.product_image_url,
              quantity:
                product.pivot?.quantity ??
                product.booking_quantity ??
                (product as any).quantity ??
                1,
              unitPrice: unitPrice,
              discount: discount,
              discountType: (product.discount_type || "").toString(),
              tax: tax,
              // Per-product line total — prefer booking item data, fall back to catalog price
              totalPrice: (() => {
                const bookingAmount =
                  product.pivot?.amount ??
                  product.booking_amount ??
                  (product as any).amount ??
                  null;
                const unitP =
                  product.pivot?.unit_price ??
                  product.booking_unit_price ??
                  (product as any).unit_price ??
                  product.converted_discounted_price ??
                  product.converted_price;
                const qty =
                  product.pivot?.quantity ??
                  product.booking_quantity ??
                  (product as any).quantity ??
                  1;
                return bookingAmount !== null && Number(bookingAmount) > 0
                  ? Number(bookingAmount)
                  : Number(unitP) * Number(qty) + tax;
              })(),
              status: booking.status.charAt(0).toUpperCase() + booking.status.slice(1),
              paymentMethod: paymentMethod,
              employeeName: employeeName,
              location: booking.location || "",
              inStoreUsage: product.in_store_usage,
            });
          });
        });

        setSales(transformedSales);
      } catch (error) {
        console.error("Error fetching sold products:", error);
        toast.error("Failed to load sold products");
      } finally {
        setIsLoading(false);
      }
    };

    fetchSoldProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only fetch once on mount

  // Unique options for dropdowns (from current data)
  const uniqueEmployees = useMemo(() => {
    const set = new Set(sales.map((s) => s.employeeName).filter(Boolean));
    return Array.from(set).sort();
  }, [sales]);
  const uniqueLocations = useMemo(() => {
    const set = new Set(sales.map((s) => s.location).filter(Boolean));
    return Array.from(set).sort();
  }, [sales]);
  const uniqueDiscountTypes = useMemo(() => {
    const set = new Set(sales.map((s) => s.discountType).filter(Boolean));
    return Array.from(set).sort();
  }, [sales]);
  // Fixed list so Cash, Card, Store Usage always appear; add any extra from data (exclude "Null")
  const paymentMethodOptions = useMemo(() => {
    const fromData = new Set(
      sales
        .map((s) => s.paymentMethod)
        .filter((m) => m && m.toLowerCase() !== "null")
    );
    const fixed: string[] = ["Cash", "Card", "Cheque", "Multiple", "Store Usage"];
    fixed.forEach((m) => fromData.add(m));
    return Array.from(fromData).sort();
  }, [sales]);

  // Filter sales
  const filteredSales = useMemo(() => {
    return sales.filter((sale) => {
      const matchesSearch =
        !searchTerm ||
        sale.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sale.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sale.orderNumber.toLowerCase().includes(searchTerm.toLowerCase());

      let matchesDate = true;
      if (dateRange.start && dateRange.end) {
        if (!sale.dateObj) matchesDate = false;
        else
          matchesDate =
            sale.dateObj >= startOfDay(dateRange.start) &&
            sale.dateObj <= endOfDay(dateRange.end);
      }

      const matchesStatus =
        statusFilter === "all" ||
        sale.status.toLowerCase() === statusFilter.toLowerCase();
      const matchesEmployee =
        employeeFilter === "all" || sale.employeeName === employeeFilter;
      const matchesLocation =
        locationFilter === "all" || sale.location === locationFilter;
      const matchesPayment =
        paymentMethodFilter === "all" ||
        sale.paymentMethod === paymentMethodFilter ||
        (paymentMethodFilter === "Store Usage" && sale.inStoreUsage === 1);
      const matchesDiscountType =
        discountTypeFilter === "all" || sale.discountType === discountTypeFilter;

      return (
        matchesSearch &&
        matchesDate &&
        matchesStatus &&
        matchesEmployee &&
        matchesLocation &&
        matchesPayment &&
        matchesDiscountType
      );
    });
  }, [
    sales,
    searchTerm,
    dateRange.start,
    dateRange.end,
    statusFilter,
    employeeFilter,
    locationFilter,
    paymentMethodFilter,
    discountTypeFilter,
  ]);

  // Paginated sales
  const paginatedSales = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredSales.slice(startIndex, endIndex);
  }, [filteredSales, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredSales.length / itemsPerPage);

  // Reset to page 1 when any filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [
    searchTerm,
    dateRange.start,
    dateRange.end,
    statusFilter,
    employeeFilter,
    locationFilter,
    paymentMethodFilter,
    discountTypeFilter,
  ]);

  const formatDate = (dateStr: string) => {
    try {
      if (!dateStr) return "N/A";

      // Backend sends date in format: "24 Jan 2026 10:00 AM"
      // Try parsing this specific format first
      const parsedDate = parse(dateStr, "dd MMM yyyy hh:mm a", new Date());
      if (isValid(parsedDate)) {
        return format(parsedDate, "dd MMM yyyy, hh:mm a");
      }

      // Fallback to ISO parsing
      const isoDate = parseISO(dateStr);
      if (isValid(isoDate)) {
        return format(isoDate, "dd MMM yyyy, hh:mm a");
      }

      // Try direct Date construction for other formats
      const d = new Date(dateStr);
      if (!isNaN(d.getTime())) {
        return format(d, "dd MMM yyyy, hh:mm a");
      }

      return dateStr;
    } catch (e) {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-4 lg:p-6">
        <div className="flex flex-col w-full h-full">
          {/* Header */}
          <PageHeader
            title="Product Sales List"
            description="View and track all product sales and retail history"
            icon={ShoppingBag}
            variant="card"
            className="mb-0"
          />

          {/* Filters row */}
          <div className="flex flex-col gap-4 py-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="inline-flex flex-shrink-0">
                <ReportDateRangePicker
                  startDate={dateRange.start}
                  endDate={dateRange.end}
                  onChange={(start, end) => setDateRange({ start, end })}
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[140px] bg-white border-gray-200">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="refunded">Refunded</SelectItem>
                </SelectContent>
              </Select>
              <Select value={employeeFilter} onValueChange={setEmployeeFilter}>
                <SelectTrigger className="w-[160px] bg-white border-gray-200">
                  <SelectValue placeholder="Employee" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All employees</SelectItem>
                  {uniqueEmployees.map((name) => (
                    <SelectItem key={name} value={name}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={locationFilter} onValueChange={setLocationFilter}>
                <SelectTrigger className="w-[160px] bg-white border-gray-200">
                  <SelectValue placeholder="Location" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All locations</SelectItem>
                  {uniqueLocations.map((loc) => (
                    <SelectItem key={loc} value={loc}>
                      {loc || "(None)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={paymentMethodFilter} onValueChange={setPaymentMethodFilter}>
                <SelectTrigger className="w-[140px] bg-white border-gray-200">
                  <SelectValue placeholder="Payment" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All payment</SelectItem>
                  {paymentMethodOptions.map((method) => (
                    <SelectItem key={method} value={method}>
                      {method}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {uniqueDiscountTypes.length > 0 && (
                <Select value={discountTypeFilter} onValueChange={setDiscountTypeFilter}>
                  <SelectTrigger className="w-[140px] bg-white border-gray-200">
                    <SelectValue placeholder="Discount type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All discount types</SelectItem>
                    {uniqueDiscountTypes.map((dt) => (
                      <SelectItem key={dt} value={dt}>
                        {dt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Toolbar: per page + search */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500 font-medium whitespace-nowrap">Show</span>
                <Select
                  value={itemsPerPage.toString()}
                  onValueChange={(value) => {
                    setItemsPerPage(Number(value));
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger className="w-[80px] bg-white border-gray-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-sm text-gray-500 font-medium whitespace-nowrap">per page</span>
              </div>
              <div className="relative w-full lg:w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by product, customer or order..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg pl-10 pr-4 py-2 text-[14px] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#885ABB] focus:border-transparent transition-all"
                />
              </div>
            </div>
          </div>

          {/* Main Content - Table */}
          <div className="pb-6">
            <div className="rounded-xl border border-gray-200 shadow-sm bg-white overflow-hidden flex flex-col">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead className="bg-[#885ABB] border-b border-gray-100">
                    <tr className="text-sm font-bold text-white tracking-wide">
                      <th className="px-6 py-4 text-left">Product</th>
                      <th className="px-6 py-4 text-left">Date</th>
                      <th className="px-6 py-4 text-left whitespace-nowrap">Order #</th>
                      <th className="px-6 py-4 text-left">Customer</th>
                      <th className="px-6 py-4 text-left">Store usage</th>
                      <th className="px-6 py-4 text-center">Qty</th>
                      <th className="px-6 py-4 text-right">Price</th>
                      <th className="px-6 py-4 text-right">Tax</th>
                      <th className="px-6 py-4 text-right">Total</th>
                      <th className="px-6 py-4 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {isLoading ? (
                      <tr>
                        <td colSpan={10} className="py-20 text-center">
                          <div className="flex flex-col items-center justify-center">
                            <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#885ABB] border-t-transparent mb-4"></div>
                            <p className="text-gray-500 font-medium">Loading sales history...</p>
                          </div>
                        </td>
                      </tr>
                    ) : paginatedSales.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="py-20 text-center text-gray-400">
                          <div className="flex flex-col items-center justify-center">
                            <ShoppingBag className="w-12 h-12 mb-3 opacity-20" />
                            <p className="text-lg font-medium text-gray-500">No product sales found</p>
                            <p className="text-sm">When you sell products in POS, they will appear here.</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      paginatedSales.map((sale, index) => (
                        <tr
                          key={`${sale.orderNumber}-${index}`}
                          className="hover:bg-purple-50/30 transition-colors group cursor-default"
                        >
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <img
                                src={sale.productImage || "/placeholder.png"}
                                alt={sale.productName}
                                className="w-10 h-10 rounded-lg object-cover flex-shrink-0 border border-gray-100"
                              />
                              <span className="text-sm text-gray-800 font-semibold truncate max-w-[180px]">
                                {sale.productName}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex flex-col min-w-[110px]">
                              <span className="text-sm text-gray-900 font-medium">
                                {(() => {
                                  try {
                                    const parsedDate = parse(sale.date, "dd MMM yyyy hh:mm a", new Date());
                                    return isValid(parsedDate) ? format(parsedDate, "MMM d, yyyy") : sale.date;
                                  } catch {
                                    return sale.date;
                                  }
                                })()}
                              </span>
                              <span className="text-xs text-gray-500">
                                {(() => {
                                  try {
                                    const parsedDate = parse(sale.date, "dd MMM yyyy hh:mm a", new Date());
                                    return isValid(parsedDate) ? format(parsedDate, "h:mm a") : "";
                                  } catch {
                                    return "";
                                  }
                                })()}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm font-bold text-[#885ABB] group-hover:underline">
                              #{sale.orderNumber}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-gray-400" />
                              <span className="text-sm font-medium text-gray-900">
                                {sale.customerName}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-700">
                              {sale.inStoreUsage === 1 ? "Used" : "—"}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-sm font-bold text-gray-700">
                              {sale.quantity}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex flex-col items-end">
                              {sale.discount > 0 && (
                                <span className="line-through text-gray-400 text-[10px]">
                                  {currencySymbol}{(sale.unitPrice + (sale.discount / sale.quantity)).toFixed(2)}
                                </span>
                              )}
                              <span className={`text-sm font-medium ${sale.discount > 0 ? "text-[#885ABB] font-bold" : "text-gray-700"}`}>
                                {currencySymbol}{sale.unitPrice.toFixed(2)}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <span className="text-sm text-gray-600">
                              {currencySymbol}{sale.tax.toFixed(2)}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <span className="text-sm font-black text-gray-900">
                              {currencySymbol}{sale.totalPrice.toFixed(2)}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${sale.status.toLowerCase() === 'completed'
                              ? 'bg-green-100 text-green-700'
                              : sale.status.toLowerCase() === 'refunded'
                                ? 'bg-red-100 text-red-700'
                                : 'bg-yellow-100 text-yellow-700'
                              }`}>
                              {sale.status}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {!isLoading && totalPages > 1 && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/50">
                  <div className="text-sm text-gray-600">
                    Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredSales.length)} of {filteredSales.length} sales
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      Previous
                    </button>
                    <div className="flex items-center gap-1.5 px-3">
                      <span className="text-sm font-bold text-[#885ABB]">{currentPage}</span>
                      <span className="text-sm text-gray-400">/</span>
                      <span className="text-sm text-gray-500">{totalPages}</span>
                    </div>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SoldProductsPage;
